import { siteLabel } from './sites';
import type { BriefCitation, BriefSourceRef } from '../hooks/useTeamTodaySurface';

/*
 * Where a brief item's [Source] button goes (phase-B walk D4: "there should ALWAYS be a source
 * button", and it is never dead).
 *
 * One target per place the item actually appeared. `href` is the real artifact when the BFF could
 * derive one; otherwise the target is the COMPACTION — our own note, which always resolves. That
 * asymmetry is the contract, not a guess: the BFF derives every citation url source-first and
 * leaves it null wherever the artifact is not reachable for this viewer (a teammate's Gmail thread
 * id is in THEIR mailbox — bug-538; ai-chat and Claude Code have owner-only platform links by
 * design). So a url-less citation is a note target, never a dead anchor.
 *
 * The list is never empty: an item with no citations at all (every legacy briefing) still resolves
 * to its own conversation.
 */

export interface SourceTarget {
  /** The picker row's line — the member note's own headline, falling back to its site. */
  label: string;
  site: string | null;
  /** The external artifact, or null when the compaction is the source. */
  href: string | null;
  /** The note behind this target, and the fallback when href is null. Null ONLY for a
   *  refs-derived row (ruling 7) — a PR the note mentions has no member note of its own. */
  conversationId: string | null;
  /** The member note's explicit visibility class (BriefCitation.visibility) — the ruled
   *  picker renders "shared with you" / "private to you" INSIDE the row (spec §5.3: one
   *  card-level line cannot represent a multi-member object with mixed visibility). Null on
   *  refs rows and the citation-less fallback. */
  visibility?: string | null;
}

const FALLBACK_LABEL = 'The note';

function usableHref(url: string | null | undefined): string | null {
  const trimmed = (url ?? '').trim();
  return trimmed ? trimmed : null;
}

export function resolveSourceTargets(
  citations: BriefCitation[] | null | undefined,
  fallbackConversationId: string,
  fallbackSite: string | null | undefined,
  // Ruling 7 (popout build spec §5.5): the card's PR deep-link rows, from the members' stored
  // refs. ADDITIVE — every pre-existing caller omits it and is byte-identical. Deduped by URL
  // against the citation-derived targets below (a ref-fused card's PR-note sibling can already
  // carry the same URL as its citation).
  sourceRefs?: BriefSourceRef[] | null,
): SourceTarget[] {
  const rows = (citations ?? []).filter(c => !!c?.conversationId);
  const source: Array<{
    conversationId: string;
    site: string | null;
    label: string | null;
    url: string | null;
    visibility: string | null;
  }> =
    rows.length > 0
      ? rows.map(c => ({
          conversationId: c.conversationId,
          site: c.site ?? null,
          label: c.label ?? null,
          url: usableHref(c.url),
          visibility: c.visibility ?? null,
        }))
      : [
          {
            conversationId: fallbackConversationId,
            site: fallbackSite ?? null,
            label: null,
            url: null,
            visibility: null,
          },
        ];

  const seen = new Set<string>();
  const targets: SourceTarget[] = [];
  for (const s of source) {
    // Two members of the same story object can land on the same artifact; the picker lists places,
    // not rows.
    const key = s.url ?? `note:${s.conversationId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    targets.push({
      label: s.label || (s.site ? siteLabel(s.site) : FALLBACK_LABEL),
      site: s.site,
      href: s.url,
      conversationId: s.conversationId,
      visibility: s.visibility,
    });
  }
  for (const ref of sourceRefs ?? []) {
    const href = usableHref(ref?.url);
    if (!href || seen.has(href)) continue;
    seen.add(href);
    targets.push({
      label: ref.key || href,
      site: 'github',
      href,
      conversationId: null,
      visibility: null,
    });
  }
  return targets;
}
